<template>
  <section id="pricing" class="py-16 lg:py-24 bg-white relative overflow-hidden">
    <div class="absolute top-0 right-1/2 translate-x-1/2 w-[800px] h-[400px] rounded-full pointer-events-none" style="background: radial-gradient(ellipse, rgba(252,231,243,0.5) 0%, transparent 70%)"></div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
        <div class="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-pink-100 text-pink-700 font-semibold text-xs sm:text-sm mb-4 sm:mb-6 border border-pink-200">
          <CreditCard class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span>השקעה שחוזרת אליכם בגדול</span>
        </div>
        <h2 class="text-3xl md:text-5xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight">השקעה קטנה שמשלמת את עצמה כבר בתור הראשון שחסכנו לכם.</h2>
        <p class="text-base sm:text-xl text-slate-600 mb-8 sm:mb-10">
          בלי הפתעות ובלי אותיות קטנות. בחרו את המסלול הנכון לעסק שלכם ותנו לו את הדחיפה שהוא צריך.
        </p>

        <!-- Monthly/Yearly Toggle -->
        <div class="flex flex-col sm:inline-flex sm:flex-row items-center p-1 sm:p-1.5 bg-slate-100 rounded-3xl sm:rounded-full border border-slate-200 shadow-inner w-full sm:w-auto gap-1 sm:gap-0 mx-auto max-w-[400px] sm:max-w-none">
          <button
            class="w-full sm:w-auto px-4 sm:px-6 py-3 rounded-full font-bold text-sm transition-all duration-300"
            :class="isYearly ? 'bg-white text-slate-900 shadow-sm transform scale-100' : 'text-slate-500 hover:text-slate-700 sm:scale-95'"
            @click="isYearly = true; posthog.capture('pricing_billing_toggled', { period: 'yearly' })"
          >
            תשלום שנתי <span class="text-emerald-500 font-black ml-1 block sm:inline text-xs sm:text-sm">(חיסכון של כ-20%)</span>
          </button>
          <button
            class="w-full sm:w-auto px-4 sm:px-6 py-3 rounded-full font-bold text-sm transition-all duration-300"
            :class="!isYearly ? 'bg-white text-slate-900 shadow-sm transform scale-100' : 'text-slate-500 hover:text-slate-700 sm:scale-95'"
            @click="isYearly = false; posthog.capture('pricing_billing_toggled', { period: 'monthly' })"
          >
            תשלום חודשי
          </button>
        </div>
      </div>

      <div class="grid lg:grid-cols-12 gap-6 sm:gap-8 items-stretch max-w-6xl mx-auto">

        <!-- Basic / Free Tier -->
        <div class="lg:col-span-4 bg-slate-50 border border-slate-200 rounded-3xl sm:rounded-[2.5rem] p-6 sm:p-8 lg:p-10 flex flex-col transform transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:shadow-xl hover:border-pink-200">
          <h3 class="text-xl sm:text-2xl font-bold text-slate-900 mb-2">מסלול בסיסי</h3>
          <p class="text-slate-500 text-xs sm:text-sm mb-6 sm:mb-8">מושלם לעסקים ממש בתחילת הדרך, כדי לחוות קצת סדר.</p>

          <div class="mb-6 sm:mb-8">
            <span class="text-4xl sm:text-5xl font-black text-slate-900">₪0</span>
            <span class="text-slate-500 font-medium text-sm sm:text-base"> / לתמיד</span>
          </div>

          <button @click="registerFree" class="w-full py-3 sm:py-4 rounded-xl font-bold text-slate-700 bg-white border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-100 transition-colors mb-6 sm:mb-8 shadow-sm text-sm sm:text-base">
            התחילו עכשיו בחינם
          </button>

          <div class="flex-1">
            <p class="text-xs sm:text-sm font-bold text-slate-900 mb-3 sm:mb-4">מה תקבלו?</p>
            <ul class="space-y-3 sm:space-y-4 text-slate-600 text-xs sm:text-sm font-medium">
              <li class="flex items-start gap-2 sm:gap-3"><CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 shrink-0"/> <span>ניהול יומן תורים (עד 25 תורים בחודש)</span></li>
              <li class="flex items-start gap-2 sm:gap-3"><CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 shrink-0"/> <span>דף נחיתה אישי ומעוצב לעסק</span></li>
              <li class="flex items-start gap-2 sm:gap-3"><CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 shrink-0"/> <span>ניהול מאגר הלקוחות שלך</span></li>
              <li class="flex items-start gap-2 sm:gap-3"><CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 shrink-0"/> <span>רשימת המתנה חכמה</span></li>
              <div class="h-px w-full bg-slate-200 my-3 sm:my-4"></div>
              <li class="flex items-start gap-2 sm:gap-3 text-slate-400 opacity-60"><X class="w-4 h-4 sm:w-5 sm:h-5 shrink-0"/> <span>ללא תזכורות וואטסאפ אוטומטיות</span></li>
              <li class="flex items-start gap-2 sm:gap-3 text-slate-400 opacity-60"><X class="w-4 h-4 sm:w-5 sm:h-5 shrink-0"/> <span>ללא סליקה, מקדמות וחשבוניות</span></li>
            </ul>
          </div>
        </div>

        <!-- Premium / Pro Tier -->
        <div class="lg:col-span-8 relative group flex flex-col mt-6 lg:mt-0">
          <div class="absolute -inset-1 rounded-[2.2rem] sm:rounded-[2.8rem] opacity-40 group-hover:opacity-70 transition duration-500" style="background: linear-gradient(to right, rgba(236,72,153,0.6), rgba(168,85,247,0.6), rgba(99,102,241,0.6)); filter: blur(12px)"></div>

          <div class="relative flex-1 bg-slate-900 border border-slate-700 rounded-3xl sm:rounded-[2.5rem] p-6 sm:p-8 lg:p-12 overflow-hidden flex flex-col md:flex-row gap-6 sm:gap-8 lg:gap-12">

            <!-- Top/Right side (Price & Action) -->
            <div class="relative z-10 flex-1 md:border-l border-slate-700/50 md:pl-8 lg:pl-12 flex flex-col justify-center order-1 md:order-2">
              <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-300 text-[10px] sm:text-xs font-bold border border-pink-500/30 mb-4 sm:mb-6 w-max shadow-inner">
                <Sparkles class="w-3 h-3 sm:w-3.5 sm:h-3.5"/>
                הבחירה של העסקים המצליחים
              </div>
              <h3 class="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-2 sm:mb-3">החבילה המלאה <span class="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">PRO</span></h3>
              <p class="text-slate-400 mb-6 sm:mb-8 text-xs sm:text-sm leading-relaxed">
                שקט נפשי באריזה דיגיטלית. מנהלת לך את העסק באוטומט, מונעת הברזות מרגיזות, ומגדילה את ההכנסות משמעותית.
              </p>

              <div class="mb-6 sm:mb-8 flex items-end gap-2">
                <span class="text-5xl sm:text-6xl font-black text-white tracking-tight">{{ isYearly ? '₪99' : '₪119' }}</span>
                <div class="flex flex-col pb-1.5 sm:pb-2">
                  <span v-if="isYearly" class="text-xs sm:text-sm text-slate-500 line-through decoration-pink-500 decoration-2">₪119</span>
                  <span class="text-slate-400 font-medium text-xs sm:text-sm"> / לחודש <template v-if="isYearly">בתשלום שנתי</template></span>
                </div>
              </div>

              <button @click="registerPro" class="w-full py-3 sm:py-4 rounded-xl font-bold text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 transition-[transform,background] shadow-lg shadow-pink-500/25 mb-3 sm:mb-4 transform hover:-translate-y-1 text-sm sm:text-base">
                התחילו 14 ימי ניסיון בחינם!
              </button>
              <p class="text-[10px] sm:text-xs text-slate-400 text-center flex items-center justify-center gap-1 sm:gap-1.5">
                <CheckCircle2 class="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-400 shrink-0" /> ללא התחייבות. ללא פרטי אשראי מראש.
              </p>
            </div>

            <!-- Bottom/Left side (Features) -->
            <div class="relative z-10 flex-[1.2] flex flex-col justify-center order-2 md:order-1 pt-6 md:pt-0 border-t border-slate-700/50 md:border-0 mt-2 md:mt-0">

              <!-- Promo Box -->
              <div class="bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-6 sm:mb-8 flex items-start gap-3 sm:gap-4 bg-white/5">
                <div class="bg-pink-500/20 p-2 sm:p-2.5 rounded-full text-pink-400 shrink-0">
                  <BadgeCheck class="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <div>
                  <h4 class="text-white font-bold mb-1 text-xs sm:text-sm">אנחנו יודעים שתאהבו אותנו</h4>
                  <p class="text-pink-200/70 text-[10px] sm:text-xs leading-relaxed">ולכן אם תחליטו שאתם לא מרוצים (מה שלא יקרה בוודאות), יש לך 60 יום להתחרט ולקבל את הכסף שלכם חזרה</p>
                </div>
              </div>

              <p class="text-xs sm:text-sm font-bold text-white mb-3 sm:mb-4">אתם מקבלים את כל הבסיס, וגם:</p>
              <ul class="space-y-3 sm:space-y-4 text-slate-300 font-medium text-xs sm:text-sm">
                <li class="flex items-center gap-2 sm:gap-3"><div class="bg-emerald-500/20 p-1 rounded-full shrink-0"><CheckCircle2 class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" /></div> <span>תזכורות וואטסאפ שמונעות הברזות</span></li>
                <li class="flex items-center gap-2 sm:gap-3"><div class="bg-emerald-500/20 p-1 rounded-full shrink-0"><CheckCircle2 class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" /></div> <span>רשימת המתנה חכמה וללא הגבלת תורים</span></li>
                <li class="flex items-center gap-2 sm:gap-3"><div class="bg-emerald-500/20 p-1 rounded-full shrink-0"><CheckCircle2 class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" /></div> <span>חיבור לסליקה וחשבוניות לתשלום קל ומהיר</span></li>
                <li class="flex items-center gap-2 sm:gap-3"><div class="bg-emerald-500/20 p-1 rounded-full shrink-0"><CheckCircle2 class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" /></div> <span>הפצת הודעות ומועדון לקוחות</span></li>
                <li class="flex items-center gap-2 sm:gap-3"><div class="bg-emerald-500/20 p-1 rounded-full shrink-0"><CheckCircle2 class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" /></div> <span>יומן לעובד נוסף כלול במחיר</span></li>
                <li class="flex items-center gap-2 sm:gap-3"><div class="bg-emerald-500/20 p-1 rounded-full shrink-0"><CheckCircle2 class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" /></div> <span>חנות אונליין, שוברים, חיבור למטא ועוד...</span></li>
              </ul>
            </div>

          </div>
        </div>

      </div>

      <!-- Clearing & Invoices Section -->
      <div class="mt-16 sm:mt-24 border-t border-slate-100 pt-12 sm:pt-16">
        <div class="text-center mb-10 sm:mb-12">
          <h3 class="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-3 sm:mb-4 tracking-tight">לגבות כסף לא צריך להיות מסובך</h3>
          <p class="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            התאימו את מסלול התשלומים והסליקה לצרכים של העסק שלכם. גביית תשלומים בלחיצת כפתור וחשבוניות שנשלחות לבד
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">

          <div class="bg-white border border-slate-200 p-6 sm:p-8 rounded-3xl shadow-sm hover:shadow-md transition-shadow flex flex-col">
            <h4 class="text-lg sm:text-xl font-bold text-slate-900 mb-2">לא סלקת, לא שילמת</h4>
            <p class="text-slate-500 text-xs sm:text-sm mb-6">למי שרוצים את האופציה לסלוק מדי פעם בראש שקט.</p>
            <div class="mb-6 flex-1">
              <span class="text-4xl font-black text-slate-900">₪0</span>
              <span class="text-slate-500 text-xs sm:text-sm"> / דמי מנוי</span>
            </div>
            <ul class="space-y-3 text-xs sm:text-sm text-slate-700 font-medium mb-6 bg-slate-50 p-4 rounded-2xl">
              <li class="flex gap-2 items-start"><CheckCircle2 class="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> <span>עמלת אשראי סטנדרטית והוגנת</span></li>
              <li class="flex gap-2 items-start"><CheckCircle2 class="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> <span>+ 1 ש"ח דמי סליקה לעסקה בלבד</span></li>
            </ul>
            <button
              @click="sendWhatsApp('972559299380', 'היי, אשמח לשמוע על מסלול ״לא סלקת לא שילמת״ '); posthog.capture('pricing_cta_clicked', { tier: 'pay_per_tx' })"
              class="w-full py-3 rounded-xl font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors text-sm sm:text-base">
              אני רוצה את המסלול הזה
            </button>
          </div>

          <div class="bg-white border border-slate-200 p-6 sm:p-8 rounded-3xl shadow-sm hover:shadow-md transition-shadow flex flex-col">
            <h4 class="text-lg sm:text-xl font-bold text-slate-900 mb-2">תוספת חשבוניות</h4>
            <p class="text-slate-500 text-xs sm:text-sm mb-6">מתאים למי שרוצים לשלוח קבלות בצורה מקצועית ללקוחות, בלי ניירת.</p>
            <div class="mb-6 flex-1">
              <span class="text-4xl font-black text-slate-900">₪20</span>
              <span class="text-slate-500 text-xs sm:text-sm"> / לחודש</span>
            </div>
            <ul class="space-y-3 text-xs sm:text-sm text-slate-700 font-medium mb-6 bg-slate-50 p-4 rounded-2xl">
              <li class="flex gap-2 items-start"><CheckCircle2 class="w-4 h-4 text-blue-500 shrink-0 mt-0.5" /> <span>חיבור מלא למערכת חשבוניות ירוקות</span></li>
              <li class="flex gap-2 items-start"><CheckCircle2 class="w-4 h-4 text-blue-500 shrink-0 mt-0.5" /> <span>תשלום סמלי נוסף לכל חשבונית שמופקת</span></li>
            </ul>
            <button
              @click="sendWhatsApp('972559299380', 'היי, אשמח לשמוע על חיבור חשבוניות '); posthog.capture('pricing_cta_clicked', { tier: 'invoices_addon' })"
              class="w-full py-3 rounded-xl font-bold text-blue-700 bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-colors text-sm sm:text-base">שדרוג לחשבוניות בלבד</button>
          </div>

          <div class="bg-slate-900 border border-slate-700 p-6 sm:p-8 rounded-3xl shadow-lg relative flex flex-col overflow-hidden group">
            <div class="absolute top-0 right-0 w-32 h-32 rounded-full" style="background: radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)"></div>
            <h4 class="text-lg sm:text-xl font-bold text-white mb-2 relative z-10">המסלול המושלם לעסקים</h4>
            <p class="text-slate-400 text-xs sm:text-sm mb-6 relative z-10">הבחירה של עסקים שסולקים ביום-יום ולא רוצות התעסקות נוספת.</p>
            <div class="mb-6 flex-1 relative z-10">
              <div class="flex items-baseline gap-2 mb-2">
                <span class="text-4xl font-black text-white">89 ש"ח</span>
              </div>
            </div>
            <ul class="space-y-3 text-xs sm:text-sm text-slate-300 font-medium mb-6 bg-slate-800/50 border border-slate-700 p-4 rounded-2xl relative z-10">
              <li class="flex gap-2 items-start"><CheckCircle2 class="w-4 h-4 text-blue-400 shrink-0 mt-0.5" /> <span>משלמים עמלת אשראי בלבד (ללא מינימום)</span></li>
              <li class="flex gap-2 items-start"><CheckCircle2 class="w-4 h-4 text-blue-400 shrink-0 mt-0.5" /> <span>חיבור מלא למערכת חשבוניות כבר בפנים</span></li>
              <li class="flex gap-2 items-start"><CheckCircle2 class="w-4 h-4 text-blue-400 shrink-0 mt-0.5" /> <strong>200 חשבוניות בחודש כלולות לגמרי!</strong></li>
            </ul>
            <button
              @click="sendWhatsApp('972559299380', 'היי, אשמח לשמוע על המסלול המושלם לעסקים '); posthog.capture('pricing_cta_clicked', { tier: 'premium_plan' })"
              class="w-full py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-500 transition-colors relative z-10 shadow-lg shadow-blue-900/50 text-sm sm:text-base">שדרגו למסלול המושלם</button>
          </div>

        </div>
      </div>

      <div class="w-full flex gap-4 justify-center items-center mt-6 sm:mt-8 ">
        <p class="text-center text-slate-400 text-xs sm:text-sm font-medium">* כל המחירים המוצגים אינם כוללים מע"מ כחוק.</p>
        <p class="text-center text-slate-400 text-xs sm:text-sm font-medium">** הקמת סולק אשראי בעלות חד״פ של 250 ש״ח.</p>

      </div>

      <!-- Quick FAQ underneath Pricing -->
      <div class="mt-16 sm:mt-20 grid md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
        <div class="bg-slate-50/50 rounded-2xl p-5 sm:p-6 border border-slate-100 hover:bg-white hover:shadow-md transition-[background-color,box-shadow]">
          <div class="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center text-lg sm:text-xl font-bold text-slate-300 mb-3 sm:mb-4 shadow-sm">1</div>
          <h4 class="font-bold text-slate-900 mb-2 text-sm sm:text-base">האם חייב להכניס אשראי לניסיון?</h4>
          <p class="text-xs sm:text-sm text-slate-600 leading-relaxed">ממש לא. אנחנו סומכים על המערכת שלנו. 14 ימי הניסיון פתוחים לגמרי בחינם, בלי לבקש מכם כרטיס אשראי מראש.</p>
        </div>
        <div class="bg-slate-50/50 rounded-2xl p-5 sm:p-6 border border-slate-100 hover:bg-white hover:shadow-md transition-[background-color,box-shadow]">
          <div class="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center text-lg sm:text-xl font-bold text-slate-300 mb-3 sm:mb-4 shadow-sm">2</div>
          <h4 class="font-bold text-slate-900 mb-2 text-sm sm:text-base">ואם לא מתאים לי? אפשר לבטל?</h4>
          <p class="text-xs sm:text-sm text-slate-600 leading-relaxed">במסלול החודשי אתם יכולים לבטל  תמיד. המסלול השנתי מעניק 20% הנחה על המחויבות לשנה, ולכן בעת ביטול תחוייבו על החלק היחסי ללא ההנחה.</p>
        </div>
        <div class="bg-slate-50/50 rounded-2xl p-5 sm:p-6 border border-slate-100 hover:bg-white hover:shadow-md transition-[background-color,box-shadow]">
          <div class="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center text-lg sm:text-xl font-bold text-slate-300 mb-3 sm:mb-4 shadow-sm">3</div>
          <h4 class="font-bold text-slate-900 mb-2 text-sm sm:text-base">הלקוחות צריכים להוריד אפליקציה?</h4>
          <p class="text-xs sm:text-sm text-slate-600 leading-relaxed">לא, וזה כל היופי. הלקוחות שלכם פשוט נכנסים ללינק המעוצב שלכם, וקובעים תור מהדפדפן שלהם בשניות.</p>
        </div>
      </div>

    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { CreditCard, Sparkles, CheckCircle2, X, BadgeCheck, Receipt } from 'lucide-vue-next';
import { sendWhatsApp } from '@torim-app/utils/lib/contact';
import posthog from 'posthog-js';
import { goTo } from '@app/utils';

const isYearly = ref<boolean>(true);

const registerPro = () => {
  posthog.capture('pricing_cta_clicked', { tier: 'pro', billing: isYearly ? 'yearly' : 'monthly' });
  goTo('register')
}

const registerFree = () => {
  posthog.capture('pricing_cta_clicked', { tier: 'free' });
  goTo('register');
}
</script>

